import { memo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import type { ProductItemOrVariant } from '../../../types';
import ChevronDownIcon from '../../../../core/components/icons/ChevronDownIcon';
import useOutsideClick from '../../../../core/hooks/dom/useOutsideClick';
import useLoginState from '../../../../core/hooks/useLoginState';
import getReplacementProducts from '../../../util/getReplacementProducts';
import styles from './ReplacementProducts.module.scss';
import ProductReplacementDetailsDropdown from './ReplacementProductsDropdown';

const ReplacementProducts = ({
    products,
}: {
    products: ProductItemOrVariant[];
}) => {
    const isLoggedIn = useLoginState();
    const [isOpen, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggle = () => setOpen(currentState => !currentState);
    const hide = () => setOpen(false);

    useOutsideClick(menuRef, hide);

    if (!isLoggedIn || !products.length) {
        return null;
    }

    const replacementProducts = getReplacementProducts(products);

    return (
        <div className={styles.container} ref={menuRef}>
            <div
                role="menu"
                tabIndex={0}
                className={clsx(styles.trigger, {
                    [styles.isTriggerActive]: isOpen,
                })}
                onClick={toggle}
                onKeyPress={e => {
                    if ([' ', 'Enter'].includes(e.key)) {
                        toggle();
                    }
                }}
                data-test-class="replacementProductsDropdownTrigger"
                data-test-state={isOpen ? 'open' : 'closed'}
            >
                <FormattedMessage id="replacement item" />
                <ChevronDownIcon
                    large
                    className={clsx(styles.icon, {
                        [styles.iconOpened]: isOpen,
                    })}
                />
            </div>
            <ProductReplacementDetailsDropdown
                isOpen={isOpen}
                replacementProducts={replacementProducts}
                onClose={hide}
            />
        </div>
    );
};

export default memo(ReplacementProducts);
